import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    type ActiveRoleAssignmentResponse,
    hasInternalAdminPrivileges,
    type PaymentRequestDetailsView,
} from '@pillar-portal/api-types';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ApiError, authApi, paymentsApi } from '@/lib/pillarApi';
import { PaymentRequestDetailView } from '@/app/payment-requests/PaymentRequestDetailView';
import {
    appendCustomerOrganizationUuid,
    getCustomerOrganizationUuidFromSearchParams,
} from '@/app/customer/customer-organization-search';

export function CustomerPaymentRequestDetailContent() {
    const params = useParams();
    const [searchParams] = useSearchParams();
    const paymentRequestUuid = params.uuid;
    const customerOrganizationUuid: string | undefined = getCustomerOrganizationUuidFromSearchParams(
        searchParams
    );

    if (paymentRequestUuid === undefined || paymentRequestUuid.trim().length === 0) {
        throw new Error('Missing payment request UUID');
    }

    const paymentRequestQuery = useQuery<PaymentRequestDetailsView>({
        queryKey: [
            'customerPaymentRequestDetails',
            paymentRequestUuid,
            customerOrganizationUuid ?? 'self',
        ],
        queryFn: async () =>
            customerOrganizationUuid === undefined
                ? await paymentsApi.getCustomerPaymentRequestDetails(paymentRequestUuid)
                : await paymentsApi.getCustomerPaymentRequestDetails(paymentRequestUuid, {
                      customer_organization_uuid: customerOrganizationUuid,
                  }),
        retry: false,
    });
    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await authApi.getActiveRoleAssignment(),
        staleTime: 30000,
    });

    if (paymentRequestQuery.isLoading) {
        return <div>Loading payment request details...</div>;
    }

    if (paymentRequestQuery.error !== null || paymentRequestQuery.data === undefined) {
        const errorMessage =
            paymentRequestQuery.error instanceof ApiError
                ? paymentRequestQuery.error.message
                : 'Failed to load payment request details.';

        return (
            <Alert variant="destructive">
                <AlertTitle>Unable to load payment request</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
        );
    }

    const payNowAction =
        paymentRequestQuery.data.public_payment_url === undefined ? undefined : (
            <Button
                type="button"
                onClick={() => {
                    window.open(
                        paymentRequestQuery.data.public_payment_url,
                        'anchorbase-customer-payment',
                        'popup,width=540,height=900'
                    );
                }}
            >
                Pay now
            </Button>
        );

    return (
        <PaymentRequestDetailView
            paymentRequest={paymentRequestQuery.data}
            pageDescription="Customer payment-request details"
            backHref={appendCustomerOrganizationUuid(
                '/customer/payment-requests',
                customerOrganizationUuid
            )}
            backTooltip="Back to Payment Requests"
            actions={payNowAction}
            showDeliveryEvents={hasInternalAdminPrivileges(activeRoleAssignmentQuery.data ?? null)}
        />
    );
}

export default function CustomerPaymentRequestDetailPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <CustomerPaymentRequestDetailContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
